import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription, timer } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { selectDataByKey } from 'src/storage/general.selectors';
import { CardDataService } from 'src/app/sarlaft/services/card-data.service';
import { MembershipService } from '@app/services/pagos/membership.service';
import { MembershipTokenResponse } from '@app/interfaces/membership.interface';
import {
  PAGO_APROBADO_NO_AP,
  PAGO_RECHAZADO_P2P,
  POLIZA_ACTIVA,
} from '@app/routes.constants';

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 40; // ~120 s

@Component({
  selector: 'app-procesando-pago',
  templateUrl: './procesando-pago.component.html',
  styleUrl: './procesando-pago.component.scss',
})
export class ProcesandoPagoComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly membershipService = inject(MembershipService);
  private readonly cardDataService = inject(CardDataService);

  private readonly subs = new Subscription();
  private attempts = 0;

  sessionId = '';
  prepolicyid = '';
  salesforceQuoteId = '';
  codAsesor = '';
  statusMessage = 'Estamos validando la información de tu pago…';

  readonly cardData = signal<any>(null);

  readonly property = toSignal(this.store.select(selectDataByKey('Property')), { initialValue: null });
  readonly tenant = toSignal(this.store.select(selectDataByKey('Tenant')), { initialValue: null });
  readonly policyHolder = toSignal(this.store.select(selectDataByKey('PolicyHolder')), { initialValue: null });
  readonly plan = toSignal(this.store.select(selectDataByKey('Plan')), { initialValue: null });
  readonly productCode = toSignal(this.store.select(selectDataByKey('ProductCode')), { initialValue: null });

  readonly planTitle = computed<string>(() => {
    const p: any = this.plan();
    const cd: any = this.cardData();
    const raw = p?.planName || cd?.planName || p?.name || p?.code || this.productCode() || '';
    return this.formatPlanName(raw);
  });

  readonly propertyAddress = computed<string>(() => {
    const p: any = this.property();
    const cd: any = this.cardData();
    return p?.PropertyAddress || p?.address || p?.direccion || cd?.PropertyAddress || 'No disponible';
  });

  readonly propertyCity = computed<string>(() => {
    const p: any = this.property();
    const cd: any = this.cardData();
    return p?.CityProperty || p?.city || p?.ciudad || cd?.PropertyCity || '';
  });

  readonly ownerName = computed<string>(() => {
    const cd: any = this.cardData();
    if (cd?.name) return cd.name;
    const ph: any = this.policyHolder();
    if (!ph) return 'No disponible';
    const fullname = [ph.FirstName, ph.SecondName, ph.LastName, ph.SecondLastName]
      .filter(Boolean).join(' ').trim();
    return fullname || 'No disponible';
  });

  readonly ownerDocumentType = computed<string>(() => {
    const ph: any = this.policyHolder();
    return String(ph?.DocumentType || 'CC');
  });

  readonly ownerDocumentNumber = computed<string>(() => {
    const ph: any = this.policyHolder();
    const raw = ph?.DocumentNumber;
    if (raw) return this.maskDocument(String(raw));
    const cd: any = this.cardData();
    return String(cd?.DocumentNumber || '');
  });

  readonly coverages = computed<string[]>(() => {
    const cd: any = this.cardData();
    if (Array.isArray(cd?.coverages) && cd.coverages.length) {
      return cd.coverages.map((n: string) => String(n)).filter(Boolean);
    }
    const p: any = this.plan();
    const list: any[] = p?.coverages ?? p?.coveragesGroup ?? [];
    return list
      .filter((c) => c?.include !== false && c?.isIncluded !== false)
      .map((c) => c?.name || '')
      .filter(Boolean);
  });

  readonly tenantName = computed<string>(() => {
    const t: any = this.tenant();
    if (!t) return '';
    const fullname = [t.FirstName, t.SecondName, t.LastName, t.SecondLastName]
      .filter(Boolean).join(' ').trim();
    return fullname;
  });

  readonly tipoPago = computed<'Mensual' | 'Anual'>(() => {
    const cd: any = this.cardData();
    const p: any = this.plan();
    const freq: string | undefined =
      cd?.payment_frequency || cd?.PaymentFrequency || p?.payment_frequency || p?.paymentFrequency;
    return freq === 'Monthly' || freq === 'Mensual' ? 'Mensual' : 'Anual';
  });

  readonly valorAPagar = computed<number>(() => {
    const cd: any = this.cardData();
    const p: any = this.plan();
    const anual = this.toNumber(
      cd?.totalAmount ?? cd?.priceAnual?.amount ??
      p?.priceAnual?.amount ?? p?.price?.full?.amount ?? p?.planPlus ?? p?.armaTuPlan ?? 0,
    );
    const mensual = this.toNumber(
      cd?.priceMensual?.amount ?? cd?.MonthlyAmount ??
      p?.priceMensual?.amount ?? p?.price?.price_monthly?.amount ?? p?.planEsencial ?? 0,
    );
    return this.tipoPago() === 'Mensual' ? mensual : anual;
  });

  readonly effectiveDate = computed<string>(() => {
    const cd: any = this.cardData();
    const p: any = this.property();
    return cd?.ContractStartDate || p?.ContractStartDate || '';
  });

  readonly paymentReference = computed<string>(() => {
    const cd: any = this.cardData();
    return cd?.idCotizacion || this.salesforceQuoteId || 'N/A';
  });

  ngOnInit(): void {
    this.sessionId = this.getSessionId();

    this.subs.add(
      this.route.queryParams.pipe(take(1)).subscribe((params) => {
        this.prepolicyid = params['prepolicyid'];
        this.codAsesor = params['codigoAsesor'] || '';
        // membresiatokenservice espera el QuoteId de Salesforce (0Q0U…), no el prepolicyid encriptado.
        this.salesforceQuoteId = params['quoteId'] || this.getStoredSfQuoteId();

        if (!this.prepolicyid) {
          console.error('[ProcesandoPago] Falta prepolicyid en query params.');
          this.redirect(PAGO_RECHAZADO_P2P);
          return;
        }

        if (!this.salesforceQuoteId) {
          console.error('[ProcesandoPago] Falta salesforceQuoteId (quoteId) en query params y sessionStorage.');
          this.redirect(PAGO_RECHAZADO_P2P);
          return;
        }

        this.loadCardData();
        this.startPolling();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private loadCardData(): void {
    const id = this.safeDecode(this.prepolicyid);
    if (!id || this.cardData()) return;
    this.subs.add(
      this.cardDataService.fetchInitialData(id, true).subscribe({
        next: (data) => this.cardData.set(data),
        error: (err) => console.error('[ProcesandoPago] Error cargando cardData:', err),
      }),
    );
  }

  private startPolling(): void {
    const poll$ = timer(0, POLL_INTERVAL_MS).pipe(
      switchMap(() => this.membershipService.getTokenResponse(this.sessionId, this.salesforceQuoteId, 'Arrendamientos')),
    );

    this.subs.add(
      poll$.subscribe({
        next: (response: MembershipTokenResponse) => {
          this.attempts += 1;
          const status = response?.data?.status;

          if (status === 'APPROVED') {
            const msg = response?.data?.message || '';
            if (msg === 'Hay una sesión aprobada que tiene póliza Vigente.') {
              this.redirect(POLIZA_ACTIVA);
            } else {
              this.redirect(PAGO_APROBADO_NO_AP);
            }
            return;
          }

          if (status === 'FAILED' || status === 'REJECTED') {
            this.redirect(PAGO_RECHAZADO_P2P);
            return;
          }

          if (this.attempts >= POLL_MAX_ATTEMPTS) {
            this.statusMessage = 'Sigue en proceso. Te enviaremos un correo cuando esté listo.';
            this.redirect(PAGO_RECHAZADO_P2P);
          }
        },
        error: (err) => {
          console.error('[ProcesandoPago] Error al consultar estado:', err);
          if (this.attempts >= POLL_MAX_ATTEMPTS) {
            this.redirect(PAGO_RECHAZADO_P2P);
          }
        },
      }),
    );
  }

  private redirect(url: string): void {
    this.subs.unsubscribe();
    const queryParams: Record<string, string> = { prepolicyid: this.prepolicyid };
    if (this.salesforceQuoteId) queryParams['quoteId'] = this.salesforceQuoteId;
    if (this.codAsesor) queryParams['codigoAsesor'] = this.codAsesor;
    // Las constantes de routes.constants quedan sin prefijo en dev (BASE=''); forzamos absoluto para matchear las rutas registradas.
    const target = url.startsWith('/') ? url : `/seguros/hogar/arriendo/cotizador/${url}`;
    this.router.navigate([target], { queryParams }).then(() => {
      setTimeout(() => window.scrollTo(0, 0), 0);
    });
  }

  private getSessionId(): string {
    return String(localStorage.getItem('session_id') || sessionStorage.getItem('session_id') || '');
  }

  private getStoredSfQuoteId(): string {
    try {
      return sessionStorage.getItem('sfQuoteId') || '';
    } catch {
      return '';
    }
  }

  private safeDecode(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  private toNumber(v: any): number {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
      const n = Number(v.replace(/[^\d.-]/g, ''));
      return isNaN(n) ? 0 : n;
    }
    return 0;
  }

  private maskDocument(value: string): string {
    const clean = String(value || '').trim();
    if (!clean) return '';
    if (clean.length <= 4) return clean;
    const start = clean.slice(0, 2);
    const end = clean.slice(-2);
    const middle = '*'.repeat(clean.length - 4);
    return `${start}${middle}${end}`;
  }

  private formatPlanName(name: string): string {
    const n = String(name || '').trim();
    if (!n) return 'Seguro de arrendamiento';
    if (/^plan\b/i.test(n) || /^arma/i.test(n)) return n;
    return `Plan ${n}`;
  }

  entendido(): void {
    window.location.href = 'https://www.sura.co/';
  }
}
